import React, { useState, KeyboardEvent } from 'react';
import { Send } from 'lucide-react';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
}

const MAX_MESSAGE_LENGTH = 1000;

const ChatInput: React.FC<ChatInputProps> = ({ onSend, disabled = false }) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSend = () => {
    const trimmed = inputValue.trim();

    if (!trimmed) {
      return;
    }

    if (trimmed.length > MAX_MESSAGE_LENGTH) {
      setError(`Message too long. Keep it under ${MAX_MESSAGE_LENGTH} characters.`);
      return;
    }

    onSend(trimmed);
    setInputValue('');
    setError(null);
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t border-dev-border bg-dev-bg p-4">
      {error && (
        <div className="mb-2 text-xs text-red-400 font-mono animate-fade-in">
          {error}
        </div>
      )}
      <div className="flex gap-2 items-start">
        <div className="flex-1 relative">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask TARS anything about Parth..."
            disabled={disabled}
            aria-label="Type your message"
            className="w-full bg-dev-bg-card border border-dev-border rounded-lg px-4 py-3 text-base sm:text-sm text-dev-text placeholder-dev-text-dimmed focus:outline-none focus:border-dev-accent resize-none font-sans transition-colors"
            rows={1}
            style={{ minHeight: '44px', maxHeight: '120px', overflowY: 'hidden' }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = Math.min(target.scrollHeight, 120) + 'px';
              target.style.overflowY = target.scrollHeight > 120 ? 'auto' : 'hidden';
            }}
          />
          <div className="absolute bottom-2 right-2 text-xs text-dev-text-dimmed font-mono">
            {inputValue.length}/{MAX_MESSAGE_LENGTH}
          </div>
        </div>
        <button
          onClick={handleSend}
          disabled={disabled || !inputValue.trim()}
          aria-label="Send message"
          className="flex-shrink-0 w-11 h-11 rounded-lg bg-dev-accent hover:bg-dev-accent-bright disabled:bg-dev-bg-card disabled:border disabled:border-dev-border disabled:text-dev-text-dimmed text-dev-bg flex items-center justify-center transition-all duration-200 shadow-glow disabled:shadow-none"
          style={{ minHeight: '44px' }}
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
};

export default ChatInput;
